import React, { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { db } from "../firebase/firestore";
import { collection, getDocs, query, where } from "firebase/firestore";
import { useUser } from "../context/userContext";
import { usePremiumCheck } from './hooks/usePremiumCheck';
import Header from "../Components/Header";

const TECHNIQUE_TABS = [
  { id: "acryl", label: "Акрил", dbValue: "Акрил" },
  { id: "watercolor", label: "Акварель", dbValue: "Акварель" },
];

const Library = () => {
  const [trainings, setTrainings] = useState([]);
  const [filteredTrainings, setFilteredTrainings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTechnique, setActiveTechnique] = useState("acryl");
  const [indicatorStyle, setIndicatorStyle] = useState({ width: 0, transform: 'translateX(0px)' });
  const containerRef = useRef(null);
  const indicatorRef = useRef(null);
  const navigate = useNavigate();
  const { premium: userIsPremium, id: userId, loading: userLoading, userType } = useUser();
  const { isPremium } = usePremiumCheck(userId, userType);

  const effectivePremium = isPremium || userIsPremium;

  // Telegram WebApp инициализация (только для Telegram пользователей)
  useEffect(() => {
    if (userType === 'telegram' && window.Telegram?.WebApp) {
      window.Telegram.WebApp.ready();
      window.Telegram.WebApp.expand();
      
      window.Telegram.WebApp.BackButton.isVisible = true;
      window.Telegram.WebApp.BackButton.offClick();
      window.Telegram.WebApp.BackButton.onClick(() => {
        console.log('Back button clicked');
        navigate(-1);
      });
    }

    return () => {
      if (userType === 'telegram' && window.Telegram?.WebApp) {
        window.Telegram.WebApp.BackButton.isVisible = false;
        window.Telegram.WebApp.BackButton.offClick();
      }
    };
  }, [navigate, userType]);

  // Загрузка тренингов с сортировкой по порядку
  useEffect(() => {
    const loadTrainings = async () => {
      try {
        const trainingsRef = collection(db, "trainings");
        const q = query(trainingsRef, where("category", "==", "Базовый курс"));
        const snap = await getDocs(q);

        const items = snap.docs.map((d) => ({
          id: d.id,
          ...d.data(),
        }));

        items.sort((a, b) => {
          if (a.order !== undefined && b.order !== undefined) {
            return a.order - b.order;
          }
          if (a.order !== undefined) return -1;
          if (b.order !== undefined) return 1;
          return new Date(b.createdAt || 0) - new Date(a.createdAt || 0);
        });

        setTrainings(items);
      } catch (e) {
        console.error("Ошибка загрузки библиотеки:", e);
      } finally {
        setLoading(false);
      }
    };

    loadTrainings();
  }, []);

  // Фильтрация по технике с сохранением порядка
  useEffect(() => {
    const activeTab = TECHNIQUE_TABS.find(tab => tab.id === activeTechnique);
    const dbValue = activeTab?.dbValue;
    
    const filtered = trainings.filter(training => 
      training.technique === dbValue
    );
    
    setFilteredTrainings(filtered);
  }, [activeTechnique, trainings]);

  // Обновление индикатора табов
  useEffect(() => {
    const updateIndicator = () => {
      const container = containerRef.current;
      const indicator = indicatorRef.current;

      if (!container || !indicator) return;

      const activeTab = container.querySelector(
        `[data-tab="${activeTechnique}"]`
      );

      if (!activeTab) return;

      const { offsetLeft, offsetWidth } = activeTab;

      setIndicatorStyle({
        width: `${offsetWidth}px`,
        transform: `translateX(${offsetLeft}px)`
      });
    };

    updateIndicator();
    const timeoutId = setTimeout(updateIndicator, 50);
    return () => clearTimeout(timeoutId);
  }, [activeTechnique]);

  // Обновление индикатора после загрузки
  useEffect(() => {
    if (!loading && containerRef.current) {
      const container = containerRef.current;
      const activeTab = container.querySelector(
        `[data-tab="${activeTechnique}"]`
      );

      if (activeTab) {
        const { offsetLeft, offsetWidth } = activeTab;
        setIndicatorStyle({
          width: `${offsetWidth}px`,
          transform: `translateX(${offsetLeft}px)`
        });
      }
    }
  }, [loading, activeTechnique]);

  const hasAccessToTraining = (training) => {
    if (!training.premium) return true;
    if (!userId) return false;
    return effectivePremium;
  };

  const handleTrainingClick = (training) => {
    const hasAccess = hasAccessToTraining(training);
    
    if (hasAccess) {
      navigate(`/training/${training.id}`);
    } else if (!userId) {
      alert("Авторизуйтесь для доступа");
    } else {
      alert("Этот тренинг доступен только для Premium пользователей");
    }
  };

  if (loading || userLoading) {
    return (
      <div className="w-full min-h-screen flex items-center justify-center text-white">
        Загрузка...
      </div>
    );
  }

  return (
    <div className="min-h-screen w-full text-white relative">
      <div
        className="fixed inset-0"
        style={{
          backgroundImage: "url('/bghome.png')",
          backgroundSize: "cover",
          backgroundPosition: "center",
          backgroundRepeat: "no-repeat",
          zIndex: -1
        }}
      />

      <div className="fixed bottom-0 left-0 right-0 h-[150px] bg-[radial-gradient(circle_at_center,rgba(120,0,255,0.15),transparent_70%)] z-0" />

      <div className="relative z-10 min-h-screen flex flex-col">
        <div className="pt-0">
          <Header />
        </div>
        
        <div 
          className="flex-1 px-4 pt-6 pb-24 overflow-y-auto"
          style={{
            scrollbarWidth: 'none',
            msOverflowStyle: 'none',
          }}
        >
          <style>{`
            div::-webkit-scrollbar {
              display: none;
            }
          `}</style>

          <h1 className="text-white text-[18px] font-semibold mb-6">
            БИБЛИОТЕКА МАСТЕР КЛАССОВ
          </h1>

          <div className="w-full mb-8">
            <div
              ref={containerRef}
              role="tablist"
              className="
                relative
                w-full
                flex
                bg-[#1E1240]
                rounded-2xl
                p-1
                overflow-hidden
                select-none
              "
              style={{
                paddingBottom: "env(safe-area-inset-bottom)",
              }}
            >
              <div
                ref={indicatorRef}
                className="
                  absolute
                  top-1
                  bottom-1
                  left-1
                  rounded-xl
                  bg-gradient-to-r
                  from-[#6C6FF8]
                  to-[#F466C8]
                  transition-all
                  duration-300
                  ease-out
                "
                style={indicatorStyle}
              />

              {TECHNIQUE_TABS.map((tab) => {
                const isActive = tab.id === activeTechnique;

                return (
                  <button
                    key={tab.id}
                    type="button"
                    role="tab"
                    aria-selected={isActive}
                    data-tab={tab.id}
                    onClick={() => setActiveTechnique(tab.id)}
                    className={`
                      relative
                      z-10
                      flex-1
                      py-3
                      text-base
                      sm:text-lg
                      font-semibold
                      rounded-xl
                      transition-colors
                      duration-200
                      focus:outline-none
                      active:scale-[0.98]
                      ${isActive ? "text-white" : "text-white/60 hover:text-white/80"}
                    `}
                  >
                    {tab.label}
                  </button>
                );
              })}
            </div>
          </div>

          <div className="flex flex-col gap-4">
            {filteredTrainings.length === 0 ? (
              <div className="text-center text-white/60 py-8">
                Нет материалов в технике "{TECHNIQUE_TABS.find(t => t.id === activeTechnique)?.label}"
              </div>
            ) : (
              filteredTrainings.map((training) => {
                const hasAccess = hasAccessToTraining(training);
                const isLocked = training.premium && !hasAccess;
                
                return (
                  <div
                    key={training.id}
                    className={`w-full rounded-[20px] border p-4 flex items-center gap-4 ${isLocked ? 'opacity-70' : ''}`}
                    style={{
                      background: "#241352",
                      borderColor: isLocked ? "#EF4444" : "rgba(255,255,255,0.2)"
                    }}
                  >
                    <div className="w-[72px] h-[72px] rounded-[12px] overflow-hidden shrink-0">
                      <img
                        src={training.image || "https://images.unsplash.com/photo-1507842217343-583bb7270b66?ixlib=rb-1.2.1&auto=format&fit=crop&w=400&q=80"}
                        alt={training.title}
                        className="w-full h-full object-cover"
                      />
                    </div>

                    <div className="flex-1 flex flex-col justify-between min-h-[72px]">
                      <div>
                        <div className="flex items-center gap-2">
                          <div
                            className="text-[16px] leading-tight font-medium break-words whitespace-normal"
                            style={{ color: isLocked ? "#9CA3AF" : "#C8AFE8" }}
                          >
                            {training.title}
                          </div>
                          {training.premium && (
                            <span className={`px-2 py-0.5 rounded-full text-xs ${hasAccess ? 'bg-yellow-500/20 text-yellow-300' : 'bg-red-500/20 text-red-300'}`}>
                              Premium
                            </span>
                          )}
                        </div>
                        
                        <div className="flex items-center gap-2 mt-1">
                          <div
                            className="text-[14px] font-medium"
                            style={{ color: isLocked ? "#EF4444" : "#34D3FF" }}
                          >
                            {isLocked ? "Заблокировано" : "Доступно"}
                          </div>
                        </div>
                      </div>

                      <div className="mt-3">
                        <button
                          className={`px-6 py-2 rounded-full text-white text-[15px] font-medium active:scale-95 transition ${isLocked ? 'cursor-not-allowed' : 'hover:opacity-90'}`}
                          style={{
                            background: isLocked ? "#6B7280" : "#6B3FD8",
                          }}
                          onClick={() => handleTrainingClick(training)}
                          disabled={isLocked}
                        >
                          {isLocked ? "Заблокировано" : "Смотреть"}
                        </button>
                        
                        {isLocked && training.premium && !effectivePremium && userId && (
                          <p className="text-xs text-yellow-400 mt-2 text-center">
                            Требуется Premium подписка
                          </p>
                        )}
                        
                        {isLocked && !userId && (
                          <p className="text-xs text-red-400 mt-2 text-center">
                            Требуется авторизация
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })
            )}
          </div>
          
          <div className="h-8"></div>
        </div>
      </div>
    </div>
  );
};

export default Library;